/**
 * Page de succès après achat de crédits
 * 
 * Cette page est affichée après un paiement Stripe réussi.
 * Redirection automatique vers le dashboard après 3 secondes.
 * 
 * Requirements: 6.4
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const CreditPurchaseSuccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [countdown, setCountdown] = useState(3);
  const hasShownToast = useRef(false);

  useEffect(() => {
    // Rafraîchir les données de crédits
    window.dispatchEvent(new Event('credits-updated'));

    // Afficher le toast de succès (une seule fois)
    if (!hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: "✅ Paiement réussi !",
        description: "Vos crédits ont été ajoutés à votre compte.",
        duration: 5000,
      });
    }

    // Démarrer le compte à rebours
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Redirection automatique vers le dashboard après 3 secondes
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 3000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides pour n'exécuter qu'une seule fois

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="text-center max-w-md w-full">
        {/* Icône de succès */}
        <div className="flex justify-center mb-6">
          <div className="h-24 w-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-pulse">
            <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
          </div>
        </div>
        
        {/* Titre */}
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Paiement réussi !
        </h1>
        
        {/* Message */}
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
          Vos crédits ont été ajoutés à votre compte avec succès.
        </p>
        
        {/* Compte à rebours */}
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 rounded-full shadow-lg">
          <span className="text-2xl animate-spin">⏳</span>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Redirection dans <span className="font-bold text-gray-900 dark:text-white">{countdown}</span> seconde{countdown > 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};
